import {Appointment} from "../Types";
import {useEffect, useState} from "react";
import Api from "../Interceptor/Api/index.ts";
import React from "react";
import {AppointmentListCard} from "../components/Appointment/AppointmentListCard.tsx";
import {Box, CircularProgress, Divider, Typography} from "@mui/material";
import PrimaryButton from "../components/Common/PrimaryButton.tsx";
import {ComponentConfig} from "../Config/ComponentConfig.js";
import {Colors} from "../Constants/Colors.js";
import HappyFamily from "../components/ProtectRoute/LoginScreen/HappyFamily.js";
import {useLocation, useNavigate} from "react-router-dom";
import HeaderWithMenu from "../components/Common/HeaderWithMenu.tsx";
import {WebRoutes} from "../Routes/WebRoutes.js";
import {toast} from "react-toastify";

export const AppointmentList = () => {
    const navigate = useNavigate();
    const [groupedAppointments, setGroupedAppointments] = useState<{
        [key: string]: { title: string; appointments: Appointment[] };
    }>({});
    const [isLoading, setIsLoading] = useState(false);

    const fetchAllAppointments = async () => {
        try {
            setIsLoading(true);
            const response = await Api.appointment.getPatientsAppointment();

            const groupedData = groupAndSortAppointments(response as Appointment[]);
            setGroupedAppointments(groupedData);
        } catch (error) {
            console.error("Error fetching appointments:", error);
            navigate("/book-doctor/step-1");
        } finally {
            setIsLoading(false);
        }
    };

    // Group, sort, and get titles for appointments
    const groupAndSortAppointments = (appointments: Appointment[]) => {
        const groupedData: {
            [key: string]: { title: string; appointments: Appointment[] };
        } = {};

        appointments.forEach((appointment) => {
            const {status} = appointment;

            // Generate title by replacing underscores with spaces and capitalizing
            const title = status
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");

            if (!groupedData[status]) {
                groupedData[status] = {
                    title,
                    appointments: [],
                };
            }

            groupedData[status].appointments.push(appointment);
        });

        Object.keys(groupedData).forEach((status) => {
            groupedData[status].appointments.sort(
                (a, b) =>
                    new Date(b.appointment_start_time).getTime() -
                    new Date(a.appointment_start_time).getTime()
            );
        });

        return groupedData;
    };

    const handleBookNewAppointment = () => {
        navigate("/book-doctor/step-2");
    };

    React.useEffect(() => {
        const userId =
            localStorage.getItem("user_id") || sessionStorage.getItem("user_id");

        if (!userId) {
            navigate(WebRoutes.BOOKING_STEP_ONE);
        }
    }, [navigate]);

    const location = useLocation();

    React.useEffect(() => {
        toast.dismiss(); // close all toasts on route change
    }, [location]);

    useEffect(() => {
        fetchAllAppointments();
    }, []);

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100vh",
                }}
            >
                <CircularProgress/>
            </Box>
        );
    }
    return (
        <Box
            className="form-container-v1"
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100vh",
                overflow: "hidden",
            }}
        >
            {/* Header and Menu */}
            <HeaderWithMenu/>

            {/* Scrollable Appointment List */}
            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                    width: "100%",
                    padding: "12px",
                }}
            >
                {Object.keys(groupedAppointments).length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            padding: "24px",
                        }}
                    >
                        {/* Illustration or Icon */}
                        <img
                            src="/images/no_appointments.png"
                            alt="No Appointments"
                            style={{
                                width: "240px",
                                height: "auto",
                            }}
                        />

                        <Typography
                            variant="bodyMedium"
                            className="body-medium"
                            sx={{
                                textAlign: "center",
                                maxWidth: "280px",
                                marginBottom: "24px",
                            }}
                        >
                            You haven’t booked any appointments. Tap the button below to
                            schedule your first appointment.
                        </Typography>
                    </Box>
                ) : (
                    Object.keys(groupedAppointments).map((status) => (
                        <div key={status}>
                            <Typography variant="bodyRegular" className="body-regular">
                                {groupedAppointments[status].title}
                            </Typography>
                            {groupedAppointments[status].appointments.map((appointment) => (
                                <AppointmentListCard
                                    key={appointment.id}
                                    appointment={appointment}
                                />
                            ))}
                            <Divider
                                sx={{
                                    margin: "16px 0",
                                    backgroundColor: Colors.greenBorderColor,
                                    height: "1px",
                                }}
                            />
                        </div>
                    ))
                )}
            </Box>

            {/* Sticky Box at Bottom */}
            <Box
                sx={{
                    width: "100%",
                    padding: "12px 0",
                    position: "sticky",
                    bottom: 0,
                    background: "white",
                    zIndex: 10,
                }}
            >
                <Box paddingX="12px">
                    <PrimaryButton
                        textVariant="subtitleMedium"
                        textClassName="subtitle-medium"
                        sx={{
                            background: ComponentConfig.primaryButtonGradient,
                        }}
                        textStyle={{
                            color: "white",
                            fontSize: ComponentConfig.fonts.subtitleMedium.fontSize,
                            fontWeight: ComponentConfig.fonts.subtitleMedium.fontWeight,
                        }}
                        text="Book New Appointment"
                        onTap={handleBookNewAppointment}
                    />
                </Box>
                <HappyFamily/>
            </Box>
        </Box>
    );
};
